import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { OverlayScrollArea } from '../components/OverlayScrollbar'
import { CatalogueTree } from '../components/CatalogueTree'
import { watchTime } from '../provider/providerClient'
import type { TitleRow } from '../provider/providerClient'
import { buildTree, shareOf, slice, sliceCaption } from '../analytics/slice'
import type { SliceAxes, TreeNode } from '../analytics/slice'
import { useLayout, usePanel } from '../nav/useBreakpoint'
import { TitleDetailPanel } from './TitleDetail'

/**
 * YOUR TITLES — v1's slice-and-dice, as a master/detail.
 *
 * ★★ TWO WAYS IN, ANSWERING TWO QUESTIONS (David, 2026-07-23). The TREE on the left goes to one
 * node and shows what that node did, with a table of everything beneath it. The RANKING on the
 * right, when nothing is selected, compares every row at the level the axes chose. v1 had both —
 * a menu tree and a `Series :` select — and they are not duplicates of each other.
 *
 * ★★ SERIES, SEASONS AND THE SUPPORTING-MATERIAL GROUP ARE ROLL-UP POINTS. They are never watched
 * themselves, so they carry the SUM of everything beneath them. "Displayed data needs to display
 * overall for all support material and then for each one individually" (David) — which is the same
 * screen every other container needs, so there is one component for all of them.
 *
 * ⚠ EVERY TITLE APPEARS, INCLUDING THE ONES NOBODY WATCHED. A list that quietly drops the quiet
 * titles reads as data loss rather than as a quiet quarter. They say "not watched" rather than
 * showing a zero pretending to be a measurement.
 *
 * ⚠ RANK IS PER LEVEL and never appears on a synthetic node — see `slice.ts`.
 */

/** ★ Primary:VeryDark, not --foreground — see the note in `Overview.tsx`. The node <h2> spreads
 *  this, so the catalogue-level heading follows it. */
const h1: CSSProperties = {
  fontSize: 'var(--fs-title)',
  fontWeight: 600,
  margin: '0 0 2px',
  color: 'var(--primary-verydark)',
}
const sub: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)', margin: '0 0 18px' }
const muted: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)' }
const card: CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
}
const rowStyle: CSSProperties = {
  padding: '7px 12px',
  fontSize: 'var(--fs-sm)',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
}
const headStyle: CSSProperties = {
  ...rowStyle,
  cursor: 'default',
  fontSize: 'var(--fs-xs)',
  color: 'var(--muted-foreground)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}
const numeric: CSSProperties = { fontFamily: 'var(--font-number)', textAlign: 'right' }
const pill: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--muted-foreground)',
  border: '1px solid var(--border)',
  borderRadius: 999,
  padding: '0 8px',
  whiteSpace: 'nowrap',
}
const linkBtn: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  background: 'var(--card)',
  color: 'var(--foreground)',
  padding: '8px 12px',
  font: 'inherit',
  cursor: 'pointer',
  minHeight: 44,
}

/** One row of the measures grid — a table row at width, a labelled card below it. */
function Measures({ row, axes, platformWatchSeconds }: { row: TitleRow; axes: SliceAxes; platformWatchSeconds: number }) {
  const share = shareOf(row, axes.scope, platformWatchSeconds)
  return (
    <>
      <span className="dash-cell" data-label="Views" style={numeric}>
        {row.Views > 0 ? row.Views.toLocaleString() : '—'}
      </span>
      <span className="dash-cell" data-label="Watch time" style={numeric}>
        {watchTime(row.WatchSeconds)}
      </span>
      <span className="dash-cell" data-label={axes.scope === 'yours' ? 'Of yours' : 'Of Yoreflix'} style={numeric}>
        {row.WatchSeconds > 0 && share !== null ? `${share}%` : '—'}
      </span>
      <span
        className="dash-cell"
        data-label="Rank"
        style={{ ...numeric, color: row.Rank === null ? 'var(--muted-foreground)' : undefined }}
      >
        {row.Rank === null ? 'not watched' : `#${row.Rank} of ${row.RankOf} ${row.RankScope}`}
      </span>
    </>
  )
}

function Head({ axes }: { axes: SliceAxes }) {
  return (
    <div className="dash-grid-row dash-grid-head" style={headStyle}>
      <span>Title</span>
      <span style={{ textAlign: 'right' }}>Views</span>
      <span style={{ textAlign: 'right' }}>Watch time</span>
      <span style={{ textAlign: 'right' }}>{axes.scope === 'yours' ? 'Of yours' : 'Of Yoreflix'}</span>
      <span style={{ textAlign: 'right' }}>Rank</span>
    </div>
  )
}

function TitleCell({ row }: { row: TitleRow }) {
  return (
    <span className="dash-cell" data-label="" style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
      <span className="dash-title-text" style={{ fontWeight: row.IsRollUp ? 600 : 400 }} title={row.Title}>
        {row.Title}
      </span>
      <span style={pill}>{row.EntryType || '—'}</span>
    </span>
  )
}

/**
 * ★★ ONE COMPONENT FOR EVERY CONTAINER. A series shows its seasons, a season its episodes, the
 * supporting-material group its items — the shape is identical, so there is one of these rather
 * than three that drift apart.
 */
function NodePanel({
  node,
  byKey,
  axes,
  platformWatchSeconds,
  period,
  onSelect,
  onClear,
}: {
  node: TreeNode
  byKey: Map<string, TitleRow>
  axes: SliceAxes
  platformWatchSeconds: number
  period: string
  onSelect: (node: TreeNode) => void
  onClear: () => void
}) {
  const own = byKey.get(node.key) ?? null
  return (
    <div>
      <button type="button" style={{ ...linkBtn, marginBottom: 12 }} onClick={onClear}>
        ← All titles
      </button>

      <h2 style={{ ...h1, fontSize: 'var(--fs-heading)' }}>{node.title}</h2>
      <p style={sub}>
        {node.entryType === 'SupportingMaterial' ? 'Supporting material' : node.entryType}
        {node.children.length > 0 && ` · ${node.children.length} beneath it`}
      </p>

      <div className="dash-tiles" style={{ marginBottom: 18 }}>
        <div style={{ ...card, padding: 12 }}>
          <div style={{ ...numeric, textAlign: 'left', fontSize: 'var(--fs-title)' }}>{node.views.toLocaleString()}</div>
          <div style={muted}>views</div>
        </div>
        <div style={{ ...card, padding: 12 }}>
          <div style={{ ...numeric, textAlign: 'left', fontSize: 'var(--fs-title)' }}>{watchTime(node.watchSeconds)}</div>
          <div style={muted}>watch time</div>
        </div>
        <div style={{ ...card, padding: 12 }}>
          <div style={{ ...numeric, textAlign: 'left', fontSize: 'var(--fs-title)' }}>
            {/* ⚠ A synthetic node has figures but no position — never a rank we invented. */}
            {node.synthetic || node.rank === null ? '—' : `#${node.rank}`}
          </div>
          <div style={muted}>{node.synthetic ? 'not ranked — a grouping' : node.rank === null ? 'not watched' : `of ${node.rankOf} ${node.rankScope}`}</div>
        </div>
      </div>

      {node.children.length > 0 && (
        <div style={{ ...card, marginBottom: 18 }}>
          <Head axes={axes} />
          {node.children.map((child) => {
            const row = byKey.get(child.key)
            return (
              <button
                key={child.key}
                type="button"
                className="admin-option dash-grid-row"
                style={rowStyle}
                onClick={() => onSelect(child)}
              >
                {row ? <TitleCell row={row} /> : (
                  <span className="dash-cell" data-label="" style={{ fontStyle: 'italic' }}>{child.title}</span>
                )}
                {row ? (
                  <Measures row={row} axes={axes} platformWatchSeconds={platformWatchSeconds} />
                ) : (
                  <>
                    <span className="dash-cell" data-label="Views" style={numeric}>{child.views.toLocaleString()}</span>
                    <span className="dash-cell" data-label="Watch time" style={numeric}>{watchTime(child.watchSeconds)}</span>
                    <span className="dash-cell" data-label="Share" style={numeric}>—</span>
                    <span className="dash-cell" data-label="Rank" style={{ ...numeric, ...muted }}>—</span>
                  </>
                )}
              </button>
            )
          })}
        </div>
      )}

      {/* ⚠ A synthetic group has no ContentKey the API knows, so it gets no day/hour chart — its
          items each have their own. Asking the server about a key we invented would 404. */}
      {own && !node.synthetic && <TitleDetailPanel contentKey={node.key} period={period} onClose={onClear} />}
    </div>
  )
}

export function TitlesScreen({
  rows,
  periodLabel,
  period,
  axes,
  platformWatchSeconds,
  error,
  linked,
}: {
  rows: TitleRow[]
  periodLabel: string
  period: string
  axes: SliceAxes
  platformWatchSeconds: number
  error: string
  linked: boolean
}) {
  const [selected, setSelected] = useState<TreeNode | null>(null)
  const layout = useLayout()
  const sheet = usePanel(layout)

  const roots = useMemo(() => buildTree(rows), [rows])
  const byKey = useMemo(() => new Map(rows.map((r) => [r.ContentKey, r])), [rows])
  const ranked = useMemo(() => slice(rows, axes, 'watch'), [rows, axes])

  /**
   * ★★ FILTER AND SERIES ARE QUESTIONS ABOUT THE LIST, SO ANSWERING ONE LEAVES THE DRILL-DOWN.
   * The detail panel reads only the scope axis, so while a node was selected those two controls
   * changed nothing whatsoever — pick a series from the tree, set Filter to anything, and the
   * page sat there (David, 2026-07-27; his call, 2026-07-27: "return to the ranking list").
   *
   * ⚠ PERIOD AND COMPARE DELIBERATELY KEEP THE SELECTION. They re-cut the node you are already
   * on — a different window, a different denominator, same subject — so throwing away the
   * drill-down would be the rude answer to a question the panel can actually answer.
   */
  const listAxis = `${axes.type}:${axes.depth}`
  const lastListAxis = useRef(listAxis)
  useEffect(() => {
    if (lastListAxis.current === listAxis) return
    lastListAxis.current = listAxis
    setSelected(null)
  }, [listAxis])

  if (error) return <div className="dash-page"><div style={{ ...muted, color: 'var(--destructive)' }}>{error}</div></div>
  if (!linked) {
    return (
      <div className="dash-page">
        <div style={muted}>
          Your account is not linked to an organization yet — email{' '}
          <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>info@yoreflix.com</a>.
        </div>
      </div>
    )
  }

  const watched = rows.filter((r) => r.WatchSeconds > 0).length

  const choose = (node: TreeNode) => {
    setSelected(node)
    // The sheet dismisses the moment you pick something — on the widths where it is a sheet.
    sheet.closeIfOverlay()
  }

  const tree = <CatalogueTree roots={roots} selectedKey={selected?.key ?? null} onSelect={choose} />

  return (
    <OverlayScrollArea axis="y">
      <div className="dash-page dash-page--wide">
        <h1 style={h1}>Your titles</h1>
        <p style={sub}>
          {rows.length} in the catalogue · {watched} watched in {periodLabel}
        </p>

        <div className="dash-split">
          {/* Desktop: the tree is a pane. Below that: a button that opens it as a sheet. */}
          {!sheet.overlay ? (
            <div style={{ ...card, padding: 8 }}>{tree}</div>
          ) : (
            <button type="button" style={{ ...linkBtn, marginBottom: 14 }} onClick={sheet.toggle}>
              Browse catalogue ▾
            </button>
          )}

          <div>
            {selected ? (
              <NodePanel
                node={selected}
                byKey={byKey}
                axes={axes}
                platformWatchSeconds={platformWatchSeconds}
                period={period}
                onSelect={choose}
                onClear={() => setSelected(null)}
              />
            ) : (
              <div>
                {/* ★ What this list is comparing, named. Two of the four Filter options
                    legitimately return the same rows for a supplier who only has series, so
                    without this the control looks broken when it is merely honest. */}
                <p style={{ ...muted, margin: '0 0 8px' }}>Comparing {sliceCaption(axes, ranked.length)}</p>
                <div style={card}>
                  <Head axes={axes} />
                  {ranked.length === 0 && (
                    <div className="dash-grid-row" style={{ ...rowStyle, cursor: 'default', borderBottom: 'none' }}>
                      <span style={muted}>Nothing matches this slice.</span>
                    </div>
                  )}
                  {ranked.map((row) => (
                    <button
                      key={row.ContentKey}
                      type="button"
                      className="admin-option dash-grid-row"
                      style={rowStyle}
                      onClick={() => {
                        const node = findNode(roots, row.ContentKey)
                        if (node) choose(node)
                      }}
                    >
                      <TitleCell row={row} />
                      <Measures row={row} axes={axes} platformWatchSeconds={platformWatchSeconds} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <p style={{ ...muted, marginTop: 12, maxWidth: 640 }}>
          Pick anything in the catalogue to see what it did and when it was watched. Series, seasons
          and supporting material show the total of everything beneath them. Rank is across
          everything watched on Yoreflix in this period, not just your own titles — and watch time
          is measured in 3-second samples, so it is approximate.
        </p>
      </div>

      {sheet.overlay && sheet.open && (
        <>
          <button type="button" className="dash-sheet-scrim" aria-label="Close catalogue" onClick={sheet.close} />
          <div className="dash-sheet">{tree}</div>
        </>
      )}
    </OverlayScrollArea>
  )
}

/** Depth-first lookup — the tree is small (a catalogue, not a filesystem). */
function findNode(nodes: TreeNode[], key: string): TreeNode | null {
  for (const node of nodes) {
    if (node.key === key) return node
    const hit = findNode(node.children, key)
    if (hit) return hit
  }
  return null
}
